import { action } from '@ember/object';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import Component from '@glimmer/component';

import type { APIData, Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

interface Args {
	model: APIData.Forecast.ConfidenceScore;
}

export default class ForecastsDataConfidenceScoreComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;

	get score() {
		return this.args.model.score;
	}

	get scoreClass() {
		const score = this.score;
		if (score <= 50) {
			return 'low';
		} else if (score < 90) {
			return 'medium';
		} else {
			return 'high';
		}
	}

	get scoreText() {
		return `${capitalize(this.scoreClass)} confidence`;
	}

	get list() {
		return this.args.model.list.map((i) => {
			const value = i.value,
				className = value === 0 ? 'yellow' : value > 0 ? 'green' : 'red',
				iconName: IconName = value === 0 ? 'Minus' : value > 0 ? 'Check' : 'AlertFill';
			return {
				text: i.text,
				class: className,
				iconName,
			};
		});
	}

	get showHelpArticle() {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	@action
	updatePointerPosition(node: HTMLElement) {
		const percToDeg = (perc: number) => {
				return perc * 360;
			},
			percToRad = (perc: number) => {
				return degToRad(percToDeg(perc));
			},
			degToRad = (deg: number) => {
				return (deg * Math.PI) / 180;
			};

		const perc = this.score / 100 / 2, // divide by 2, bcos half circle
			rad = percToRad(perc),
			arcThickness = 6,
			allWidth = (node.parentNode as HTMLElement).offsetWidth,
			arcRadius = allWidth / 2 - arcThickness / 2,
			padding = arcThickness / 2,
			centerX = padding + arcRadius,
			centerY = padding + arcRadius,
			pointerRadius = node.offsetHeight / 2;

		const xFromCenter = -1 * arcRadius * Math.cos(rad), // we flip the sign bcos our 0 is on left not right
			yFromCenter = arcRadius * Math.sin(rad),
			x = centerX + xFromCenter - pointerRadius,
			y = centerY - yFromCenter - pointerRadius;

		node.style.left = `${x}px`;
		node.style.top = `${y}px`;
	}
	<template>
		<div class="module-box confidence-score-box">
			<div class="boxes-wrap">
				<div class="box__left box__left--score-{{this.scoreClass}}">
					<img
						src="/images/forecasts/confidence-score-bg.svg"
						width="100%"
						alt="Confidence score scale"
					/>
					<span
						class="pointer"
						{{didInsert this.updatePointerPosition}}
						{{didUpdate this.updatePointerPosition @model.score}}
					></span>
					<div class="ct">
						<strong class="score">{{this.score}}</strong>
						<span class="text">{{this.scoreText}}</span>
					</div>
				</div>
				<div class="box__middle">
					<h4>Forecast confidence score</h4>
					<p>
						The score reflects the confidence level of the calculated forecast. It takes
						into account the amount of available historical data, length of the
						forecasted period, number of anomalies detected in the data, volatility, and
						settings of forecasting parameters.
						{{#if this.showHelpArticle}}
							<a
								href="https://help.databox.com/databox-forecasting-methodology"
								target="_blank"
							>
								Learn more
							</a>
						{{/if}}
					</p>
				</div>
				<div class="box__right">
					<ul>
						{{#each this.list as |item|}}
							<li class={{item.class}}>
								<CommonUiIconComponent @name={{item.iconName}} @size="xs" />
								{{item.text}}
							</li>
						{{/each}}
					</ul>
				</div>
			</div>
		</div>
	</template>
}
